import React, {useEffect, useState} from "react";
import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from "@mui/material";
import {
    wareHouseListApiCall,
    driverDetailsApiCall,
} from "../../controllers/location_repository_controller";
import {LocationListModel} from "../../models/location_list_model";
import {DriverListModel} from "../../models/driver_list_model";
import Skeleton from "@mui/material/Skeleton";
import {convertTo12Hour} from "../../../utils/date_time_convert_utils";
import {LocationDetailsDialog} from "./location_edit_dialog";
import {ReactComponent as EditIcon} from "../../../assets/images/edit_icon.svg";

const LocationRepositoryBody = ({
                                    query,
                                    pageSize,
                                    currentPage,
                                    onCountUpdate,
                                }) => {

    let firstRender = true;

    const [isBusy, setIsBusy] = useState(false);
    const [locationList, setLocationList] = useState([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [driverList, setDriverlist] = useState([]);
    const [selectedDriverName, setSelectedDriverName] = useState("");
    const [selectedDriverUid, setSelectedDriverUid] = useState("");

    const columns = [
        {id: "locationName", label: "CLIENT LOCATION", width: "180px"},
        {id: "locationCode", label: "LOCATION CODE", width: "100px"},
        {id: "locationAddress", label: "ADDRESS", width: "100px"},
        {id: "locationCountryCode", label: "COUNTRY CODE", width: "100px"},
        {id: "locationType", label: "TYPE", width: "80px"},
        {id: "assignedDriver", label: "ASSIGNED DRIVER", width: "120px"},
        {id: "contactDetails", label: "CONTACT DETAILS", width: "200px"},
        {id: "pickupTime", label: "TIME OF VISIT", width: "80px"},
        {id: "action", label: "ACTION", width: "80px"},
    ];

    const warehousesListApiCalling = async () => {
        try {
            setIsBusy(true);
            const response = await wareHouseListApiCall(query, pageSize, currentPage);
            if (response) {
                const locationListModel = LocationListModel.fromJson(response);
                setLocationList(locationListModel.warehouseList);
                onCountUpdate(locationListModel.count, locationListModel.total);
            }
        } catch (e) {
            console.error(e);
            setLocationList([]);
            onCountUpdate(0, 1);
        } finally {
            setIsBusy(false);
        }
    };
    const driverListApiCalling = async () => {
        try {
            const response = await driverDetailsApiCall(query, pageSize, currentPage);
            if (response) {
                const driverListModel = DriverListModel.fromJson(response);
                setDriverlist(driverListModel.results);
            }
        } catch (e) {
            console.error(e);
            setDriverlist([]);
        }
    };

    useEffect(() => {
        if (firstRender) {
            driverListApiCalling();
        }

    }, []);

    useEffect(() => {
        if (firstRender) {
            warehousesListApiCalling();
        }
        firstRender = false;

    }, [query, pageSize, currentPage]);

    const handleEditClick = (location) => {
        setSelectedLocation(location);
        let driberName;
        if (
            location?.locationTypeCode === "VL" &&
            location?.assignedDriverUid !== null
        ) {
            driverList.forEach((driver) => {
                if (driver?.uid === location?.assignedDriverUid) {
                    driberName = driver.name;
                    setSelectedDriverName(driver.name);
                    setSelectedDriverUid(driver.uid);
                    return;
                }
            });
        }

        setIsDialogOpen(true);
    };

    const handleCloseDialog = (event, reason, callListingApi = false) => {
        ///to prevent dispose the popup on click of outside
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
        }
        setIsDialogOpen(false);
        setSelectedLocation(null);
        setSelectedDriverName("");
        setSelectedDriverUid("");

        if (callListingApi) {
            warehousesListApiCalling();
        }
    };

    const renderCell = (column, row) => {
        const cellStyle = {
            display: "flex",
            alignItems: "left",
            flexDirection: "column",
            width: `${column.width}`,
            whiteSpace: "normal",
            wordWrap: "break-word",
        };
        const locationNameStyle = {fontSize: "16px", fontWeight: "600"};
        switch (column.id) {
            case "contactDetails":
                return (
                    <div style={{...cellStyle, gap: "5px"}}>
                        <div>{row.phoneNumber ?? ""}</div>
                        <div>{row.email ?? ""}</div>
                    </div>
                );
            case "pickupTime":
                return (
                    <div style={{...cellStyle}}>
                        <div> {convertTo12Hour(row.pickupTime) || "-"} </div>
                    </div>
                );
            case "action":
                return (
                    <EditIcon
                        onClick={() => handleEditClick(row)}
                        style={{
                            width: "17px",
                            height: "17px",
                            cursor: "pointer",
                        }}
                    />
                );
            default:
                return (
                    <span
                        style={
                            column.id === "locationName"
                                ? {...locationNameStyle, ...cellStyle}
                                : {...cellStyle}
                        }
                    >
            {row[column.id] || "-"}
          </span>
                );
        }
    };

    const tableCellSkeleton = (
        <Skeleton variant="rectangular" width={100} height={30}/>
    );

    return (
        <div
            style={{
                display: "flex",
                padding: "10px 20px",
                ...(!localStorage.getItem('isFromHostApplication') ? {height: "calc(90vh - 140px)",} : {height: "calc(85vh - 140px)",})
            }}
        >
            <TableContainer
                style={{
                    borderTopLeftRadius: "16px",
                    borderTopRightRadius: "16px",
                    borderRadius: "16px",
                    border: "1px solid #E0E2E6",
                }}
            >
                <Table>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    align={column.id === "action" ? "center" : "left"}
                                    style={{
                                        width: column.width,
                                        height: "43px",
                                        backgroundColor: "#EAECEE",
                                        borderBottom: "1px solid #E5E5E5",
                                        padding: "6px 20px",
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 100,
                                        fontFamily: "Inter, Arial, sans-serif",
                                        color: "#6F727A",
                                        fontWeight: "600",
                                        fontSize: "12px",
                                    }}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isBusy ? (
                            [...Array(20)].map((_, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    {columns.map((column, colIndex) => (
                                        <TableCell
                                            key={`${rowIndex}-${colIndex}`}
                                            align={column.id === "action" ? "center" : "left"}
                                        >
                                            {tableCellSkeleton}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : locationList.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center">
                                    No Record found
                                </TableCell>
                            </TableRow>
                        ) : (
                            locationList.map((row, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    {columns.map((column, colIndex) => (
                                        <TableCell
                                            key={`${rowIndex}-${colIndex}`}
                                            align={column.id === "action" ? "center" : "left"}
                                            style={{
                                                padding: "6px 20px",
                                                height: "38px",
                                                fontFamily: "Inter, Arial, sans-serif",
                                                borderBottom: "1px solid #E0E2E6",
                                                color:
                                                    column.id === "status"
                                                        ? row.status.toLowerCase() === "active"
                                                            ? "green"
                                                            : "red"
                                                        : "black",
                                            }}
                                        >
                                            {renderCell(column, row)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <LocationDetailsDialog
                isDialogOpen={isDialogOpen}
                handleCloseDialog={handleCloseDialog}
                locationDetails={selectedLocation}
                driverlist={driverList}
                driverName={selectedDriverName}
                driveUid={selectedDriverUid}
            />
        </div>
    );
};

export default LocationRepositoryBody;
